import { useState, useCallback, useMemo, useEffect } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import downloadPDFFormSchema from "./DownloadPDFFormSchema";

import {
  isValidEmail,
  isValidMobile,
  matchCountryRegex,
  timezone,
} from "@/utils";
import { useAppDispatch } from "@/hooks";
import { DEFAULT_URL_ARGUMENTS } from "@/constants";

import * as actions from "@/redux/LandingPageReducer";

interface useDownloadPDFFormI {
  onClose: () => void;
  campaignSlug: string;
}

const useDownloadPDFForm = ({ onClose, campaignSlug }: useDownloadPDFFormI) => {
  const dispatch = useAppDispatch();

  let urlParams = "";
  if (typeof window !== "undefined") {
    urlParams = window.location.search;
  }
  const urlsArguments = urlParams || DEFAULT_URL_ARGUMENTS;

  const [isLoading, setIsLoading] = useState(false);
  const [showSuccessScreen, setShowSuccessScreen] = useState<{
    mobile: string;
    email: string;
  } | null>(null);

  const resolver = useMemo(() => yupResolver(downloadPDFFormSchema), []);

  const defaultValues = useMemo(
    () => ({
      name: "",
      mobileCountryCode: "+91",
      mobile: "",
      email: "",
      formError: "",
    }),
    []
  );

  const methods = useForm({
    resolver,
    defaultValues,
    mode: "onBlur",
  });

  const {
    getValues,
    formState: { errors },
    watch,
    clearErrors,
  } = methods;

  const { mobile, email } = watch();

  useEffect(() => {
    if (errors?.formError) {
      if (mobile || email) {
        clearErrors("formError");
      }
    }
  }, [mobile, email, errors, clearErrors]);

  const mapData = useCallback(
    (type?: string) => {
      const { name, mobileCountryCode, mobile, email } = getValues();

      if (type === "campaign-lead") {
        return {
          ...(name && { name: name }),
          ...(mobile &&
            mobileCountryCode && {
              primary_contact: `${mobileCountryCode}${mobile}`,
            }),
          ...(email && { email: email }),
          ...(urlsArguments && { urls_arguments: urlsArguments }),
          campaign_slug: campaignSlug,
          lead_type: "CAMPAIGN_LEAD",
        };
      } else {
        return {
          ...(name && { name: name }),
          ...(mobileCountryCode && {
            phone_prefix: mobileCountryCode,
          }),
          ...(mobile && { mobile: mobile }),
          ...(email && { email: email }),
          ...(urlsArguments && { urls_arguments: urlsArguments }),
          timezone,
        };
      }
    },
    [getValues, urlsArguments, campaignSlug]
  );

  const handleBlur = () => {
    const { mobile, mobileCountryCode, email } = getValues();

    const isValidContactDetail =
      (isValidMobile(mobile) && matchCountryRegex(mobileCountryCode, mobile)) ||
      isValidEmail(email);

    if (isValidContactDetail) {
      const apiData = {
        is_lead: true,
        ...mapData(),
      };
      dispatch(actions.saveLead({ data: apiData }));
    }
  };

  const onSubmit = () => {
    if (Object.keys(errors)?.length === 0) {
      setIsLoading(true);
      const apiData = mapData("campaign-lead");

      dispatch(actions.postCampaignLead({ data: apiData })).then(() => {
        setIsLoading(false);
        setShowSuccessScreen({
          mobile: apiData.primary_contact,
          email: apiData.email,
        });
      });
    }
  };

  useEffect(() => {
    if (showSuccessScreen === true) {
      setTimeout(() => {
        setShowSuccessScreen(false);
        onClose();
      }, 60000);
    }
  }, [showSuccessScreen, onClose]);

  return {
    isLoading,
    handleBlur,
    onSubmit,
    methods,
    showSuccessScreen,
  };
};

export default useDownloadPDFForm;
